import useSessionStore from "@features/session/hooks/useSessionStore";
import Box from "@memorang/ui/components/Box";
import SessionShell from "@memorang/ui/components/StudySession/SessionShell";
import { type ReactNode, type RefObject, useCallback } from "react";
import type { FlatList } from "react-native";
import FormativeAppBarContainer from "./FormativeAppBarContainer";
import StudySessionBottombarContainer from "./StudySessionBottombarContainer";
import SummativeAppBarContainer from "./SummativeAppBarContainer";

type Props = {
	children: ReactNode;
	showStartDialog?: boolean;
	showAudioPlaybackTestDialog?: boolean;
	onCloseAudioPlaybackTestDialog?: () => void;
	flashlistRef?: RefObject<FlatList | null>;
};

type TopBarProps = {
	isSummativeTest: boolean;
	showStartDialog?: boolean;
	showAudioPlaybackTestDialog?: boolean;
	onCloseAudioPlaybackTestDialog?: () => void;
};

const TopBar = ({
	isSummativeTest,
	showStartDialog,
	showAudioPlaybackTestDialog,
	onCloseAudioPlaybackTestDialog,
}: TopBarProps) => {
	if (isSummativeTest && onCloseAudioPlaybackTestDialog) {
		return (
			<SummativeAppBarContainer
				showStartDialog={showStartDialog}
				showAudioPlaybackTestDialog={showAudioPlaybackTestDialog}
				onCloseAudioPlaybackTestDialog={onCloseAudioPlaybackTestDialog}
			/>
		);
	}
	return (
		<FormativeAppBarContainer
			showStartDialog={showStartDialog}
			showAudioPlaybackTestDialog={showAudioPlaybackTestDialog}
		/>
	);
};

const StudySessionShellContainer = ({
	children,
	showStartDialog,
	showAudioPlaybackTestDialog,
	onCloseAudioPlaybackTestDialog,
	flashlistRef,
}: Props) => {
	const isSummativeTest = useSessionStore((s) => s.isSummativeTest);
	const summative = Boolean(isSummativeTest);

	const renderTopBar = useCallback(
		() => (
			<TopBar
				isSummativeTest={summative}
				showStartDialog={showStartDialog}
				showAudioPlaybackTestDialog={showAudioPlaybackTestDialog}
				onCloseAudioPlaybackTestDialog={onCloseAudioPlaybackTestDialog}
			/>
		),
		[
			summative,
			showStartDialog,
			showAudioPlaybackTestDialog,
			onCloseAudioPlaybackTestDialog,
		],
	);

	const renderBottomBar = useCallback(
		() => <StudySessionBottombarContainer flashlistRef={flashlistRef} />,
		[flashlistRef],
	);

	return (
		<Box flex={1}>
			<SessionShell
				renderTopBar={renderTopBar}
				renderBottomBar={renderBottomBar}
			>
				{children}
			</SessionShell>
		</Box>
	);
};

export default StudySessionShellContainer;
